import { PokemonBoxData } from "types/web-app";
import cn from "classnames";
import { useDroppable } from "@dnd-kit/core";

interface BoxTabsProps {
  boxes: PokemonBoxData[];
  selectedBoxId: string;
  onSelectBox: (boxId: string) => void;
  onAddBox: () => void;
  className?: string;
}

const DroppableTab = ({
  boxId,
  children,
  className,
  style,
  onClick,
  title,
}: {
  boxId: string;
  children: React.ReactNode;
  className?: string;
  style?: React.CSSProperties;
  onClick?: () => void;
  title?: string;
}) => {
  const { isOver, setNodeRef } = useDroppable({
    id: `box-${boxId}`,
    data: { boxId },
  });

  return (
    <button
      ref={setNodeRef}
      onClick={onClick}
      className={cn(className, {
        "border-white shadow-[0_-4px_15px_rgba(255,255,255,0.4)] scale-110 z-30 -mb-[4px] pb-[4px] opacity-100 ring-2 ring-pk-orange-4 ring-offset-2 ring-offset-black bg-white/20":
          isOver,
      })}
      style={style}
      title={title}
    >
      {children}
    </button>
  );
};

export const BoxTabs = ({
  boxes,
  selectedBoxId,
  onSelectBox,
  onAddBox,
  className,
}: BoxTabsProps) => {
  return (
    <div className={cn("flex items-center gap-0 w-full", className)}>
      <div className="flex-1 flex flex-row items-center -gap-2 overflow-x-auto scrollbar-hide min-h-[50px] ">
        {/* ALL Tab */}
        <DroppableTab
          boxId="all"
          onClick={() => onSelectBox("all")}
          className={cn(
            "shrink-0 w-12 h-12   rounded-t-lg flex items-center justify-center transition-all border-x-2 border-t-2 relative overflow-hidden",
            selectedBoxId === "all"
              ? "border-white shadow-[0_-4px_10px_rgba(255,255,255,0.2)] scale-105 z-11 -mb-[4px] pb-[4px] bg-[#333]"
              : "border-white/20 hover:border-white/50 opacity-70 hover:opacity-100 bg-[#333]/50",
          )}
        >
          <span className="text-[10px] font-bold text-white">ALL</span>
        </DroppableTab>

        {/* Box Tabs */}
        {boxes.map((box) => (
          <DroppableTab
            key={box.id}
            boxId={box.id}
            onClick={() => onSelectBox(box.id)}
            className={cn(
              "shrink-0 w-11 h-12 rounded-t-lg flex items-center justify-center transition-all border-x-2 border-t-2 relative overflow-hidden",
              selectedBoxId === box.id
                ? "border-white shadow-[0_-4px_10px_rgba(255,255,255,0.2)] scale-105 z-11 -mb-[4px] pb-[4px]"
                : "border-white/20 hover:border-white/50 opacity-70 hover:opacity-100",
            )}
            style={{
              backgroundColor:
                selectedBoxId === box.id ? box.color : `${box.color}80`,
              borderColor: selectedBoxId === box.id ? "white" : undefined,
            }}
            title={box.name}
          >
            {box.icon && (
              <img
                src={`/assets/icons/boxes/${box.icon}.svg`}
                className="w-8 h-8 fill-white stroke-white drop-shadow-md"
                alt={box.name}
              />
            )}
          </DroppableTab>
        ))}
      </div>

      {/* Add Button - Fixed to the right */}
      <button
        onClick={onAddBox}
        className="shrink-0 text-sm w-10 translate-y-2 text-center h-12 rounded-t-lg flex items-center justify-center bg-white/10 hover:bg-white/20 border-2 border-dashed border-white/30 hover:border-white/50 transition-all text-white/50 hover:text-white"
        title="Manage Squads"
      >
        <span className="text-2xl translate-x-0.5">+</span>
      </button>
    </div>
  );
};
